import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Button, Card, Heading, Text } from '../ui/index.js';
import { api } from '../api/client.js';
import { keys, useAccess } from '../api/hooks.js';
import { Focusable } from '../components/focus/Focusable.js';
import { EmptyState, Loading } from '../components/layout/Loading.js';
import { BarList, Funnel, Sparkline, StackedArea } from '../components/admin/charts.js';

export const Route = createFileRoute('/admin/insights')({ component: AdminInsights });

/**
 * The one screen that answers "how is this going".
 *
 * Built around a single question per section, in the order a solo founder actually asks them:
 * is it growing, who are they, where do they fall out, what are they playing, and what is
 * broken. Everything carries a comparison against the previous window of the same length,
 * because a number with no direction is not a decision.
 *
 * Two sources sit behind it and the screen is honest about which is which. The exact half comes
 * from D1 and is always there. The traffic half comes from Analytics Engine, which needs a token
 * that an environment may not have, so those panels can come back "not measured" — which is
 * drawn as its own state rather than as a zero.
 */

interface TrafficDay {
  day: string;
  visitors: number;
  sessions: number;
  signedIn: number;
  views: number;
}

interface Totals {
  users: number;
  members: number;
  newUsers: number;
  playSessions: number;
  playtimeHours: number;
  players: number;
  installs: number;
  installFailures: number;
  launchRefusals: number;
  visitors: number | null;
  sessions: number | null;
  signedInDau?: number | null;
}

interface Overview {
  range: { days: number; from: string; to: string };
  live: boolean;
  totals: Totals;
  previous: Totals;
  series: TrafficDay[];
  playtimeByDay: Array<{ day: string; hours: number; sessions: number }>;
  signupsByDay: Array<{ day: string; count: number }>;
  funnel: Array<{ key: string; label: string; count: number | null }>;
  platforms: Array<{ key: string; visitors: number; views: number }>;
  countries: Array<{ key: string; visitors: number; views: number }>;
  installFailures: Array<{ reason: string; count: number }>;
  launchRefusals: Array<{ reason: string; count: number }>;
}

interface GameStats {
  gameId: string;
  title: string;
  slug: string;
  launches: number;
  players: number;
  sessions: number;
  playtimeHours: number;
  medianSessionMin: number | null;
  installs: number;
  installFailures: number;
  launchRefusals: number;
}

interface PageStats {
  route: string;
  views: number;
  visitors: number;
  signedInShare: number;
}

const RANGES = [7, 28, 90] as const;

function AdminInsights() {
  const { isAdmin } = useAccess();
  const [days, setDays] = React.useState<number>(28);

  const overview = useQuery({
    queryKey: [...keys.admin.insights, days],
    queryFn: () => api.get<Overview>('/admin/insights', { days }),
    enabled: isAdmin,
    refetchInterval: 5 * 60_000,
  });

  const games = useQuery({
    queryKey: [...keys.admin.insights, 'games', days],
    queryFn: () => api.get<{ games: GameStats[] }>('/admin/insights/games', { days }),
    enabled: isAdmin,
  });

  const pages = useQuery({
    queryKey: [...keys.admin.insights, 'pages', days],
    queryFn: () => api.get<{ pages: PageStats[] | null }>('/admin/insights/pages', { days }),
    enabled: isAdmin,
  });

  if (!isAdmin) return <EmptyState title="You cannot do that." />;
  if (overview.isLoading || !overview.data) return <Loading />;

  const data = overview.data;
  const visitorSeries = data.series.map((d) => d.visitors);
  const anonymous = data.series.map((d) => Math.max(0, d.visitors - d.signedIn));

  return (
    <div className="gb-stack">
      <div className="gb-seat">
        <Heading size="5">{'Insights'}</Heading>
        <span className="gb-seat__spacer" />
        {RANGES.map((option) => (
          <Focusable key={option} onSelect={() => setDays(option)} selectLabel={`${option} days`}>
            <Button size="1" variant={days === option ? 'solid' : 'surface'}>
              {`${option}d`}
            </Button>
          </Focusable>
        ))}
      </div>

      {!data.live ? (
        <Card size="2">
          <Text size="2" color="gray">
            {
              'Traffic and in-game panels are off: this environment has no Analytics Engine token. Members, playtime and games below are exact and unaffected.'
            }
          </Text>
        </Card>
      ) : null}

      {/* 1. Is it growing? */}
      <div className="gb-grid">
        <Stat
          label="Members"
          value={data.totals.members}
          previous={data.previous.members}
          series={null}
        />
        <Stat
          label="New accounts"
          value={data.totals.newUsers}
          previous={data.previous.newUsers}
          series={data.signupsByDay.map((d) => d.count)}
          slot={1}
        />
        <Stat
          label="Visitors"
          value={data.totals.visitors}
          previous={data.previous.visitors}
          series={visitorSeries}
          slot={2}
        />
        <Stat
          label="Sessions"
          value={data.totals.sessions}
          previous={data.previous.sessions}
          series={data.series.map((d) => d.sessions)}
          slot={2}
        />
        <Stat
          label="Players"
          value={data.totals.players}
          previous={data.previous.players}
          series={null}
        />
        <Stat
          label="Hours played"
          value={data.totals.playtimeHours}
          previous={data.previous.playtimeHours}
          series={data.playtimeByDay.map((d) => d.hours)}
          slot={1}
        />
      </div>

      <Card size="3" className="gb-stack gb-stack--tight">
        <Heading size="4">{'Who is here'}</Heading>
        {data.series.length > 1 ? (
          <StackedArea
            days={data.series.map((d) => d.day)}
            series={[
              {
                key: 'signed_in',
                label: 'Signed in',
                slot: 2,
                values: data.series.map((d) => d.signedIn),
              },
              { key: 'anon', label: 'Signed out', slot: 1, values: anonymous },
            ]}
          />
        ) : (
          <Text color="gray">{data.live ? 'Not enough days yet.' : 'Not measured here.'}</Text>
        )}
      </Card>

      {/* 2. Where do they fall out? */}
      <Card size="3" className="gb-stack gb-stack--tight">
        <Heading size="4">{'From visit to play'}</Heading>
        <Text size="1" color="gray">
          {
            'Counts at each stage over the same window, not one person followed through it: signed-out visitors are never linked to the account they may later make.'
          }
        </Text>
        <Funnel steps={data.funnel} />
      </Card>

      {/* 3. What are they playing? The table this whole screen exists for. */}
      <Card size="3" className="gb-stack gb-stack--tight">
        <Heading size="4">{'Games'}</Heading>
        {games.isLoading ? (
          <Loading />
        ) : (games.data?.games.length ?? 0) === 0 ? (
          <Text color="gray">{'No games yet.'}</Text>
        ) : (
          <div className="gb-table__scroll">
            <table className="gb-table">
              <thead>
                <tr>
                  <th>{'Game'}</th>
                  <th className="gb-table__num">{'Hours'}</th>
                  <th className="gb-table__num">{'Players'}</th>
                  <th className="gb-table__num">{'Sessions'}</th>
                  <th className="gb-table__num">{'Median'}</th>
                  <th className="gb-table__num">{'Launches'}</th>
                  <th className="gb-table__num">{'Installs'}</th>
                  <th className="gb-table__num">{'Failed'}</th>
                  <th className="gb-table__num">{'Refused'}</th>
                </tr>
              </thead>
              <tbody>
                {games.data?.games.map((game) => (
                  <tr key={game.gameId}>
                    <td>{game.title}</td>
                    <td className="gb-table__num">{game.playtimeHours.toLocaleString()}</td>
                    <td className="gb-table__num">{game.players}</td>
                    <td className="gb-table__num">{game.sessions}</td>
                    <td className="gb-table__num">
                      {game.medianSessionMin === null ? '—' : `${game.medianSessionMin}m`}
                    </td>
                    <td className="gb-table__num">{game.launches}</td>
                    <td className="gb-table__num">{game.installs}</td>
                    <td className="gb-table__num">{game.installFailures}</td>
                    <td className="gb-table__num">{game.launchRefusals}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* 4. Which screens do they open? */}
      <Card size="3" className="gb-stack gb-stack--tight">
        <Heading size="4">{'Screens'}</Heading>
        {pages.data?.pages === null ? (
          <Text color="gray">{'Not measured here.'}</Text>
        ) : (
          <BarList
            slot={2}
            limit={12}
            data={(pages.data?.pages ?? []).map((page) => ({
              key: page.route,
              label: page.route,
              value: page.views,
              display: `${page.views.toLocaleString()} · ${Math.round(page.signedInShare * 100)}% in`,
            }))}
          />
        )}
      </Card>

      <div className="gb-grid">
        <Card size="3" className="gb-stack gb-stack--tight">
          <Heading size="4">{'Platforms'}</Heading>
          <BarList
            slot={1}
            data={data.platforms.map((row) => ({
              key: row.key,
              label: row.key,
              value: row.views,
              display: row.views.toLocaleString(),
            }))}
            empty={data.live ? 'Nothing yet' : 'Not measured here'}
          />
        </Card>

        <Card size="3" className="gb-stack gb-stack--tight">
          <Heading size="4">{'Countries'}</Heading>
          <BarList
            slot={2}
            data={data.countries.map((row) => ({
              key: row.key,
              label: row.key,
              value: row.views,
              display: row.views.toLocaleString(),
            }))}
            empty={data.live ? 'Nothing yet' : 'Not measured here'}
          />
        </Card>
      </div>

      {/* 5. What is broken? Exact, from D1, and invisible before any of this existed. */}
      <div className="gb-grid">
        <Card size="3" className="gb-stack gb-stack--tight">
          <Heading size="4">{'Installs that failed'}</Heading>
          <BarList
            slot={3}
            data={data.installFailures.map((row) => ({
              key: row.reason,
              label: row.reason,
              value: row.count,
              display: String(row.count),
            }))}
            empty="None, which is the right number"
          />
        </Card>

        <Card size="3" className="gb-stack gb-stack--tight">
          <Heading size="4">{'Launches refused'}</Heading>
          <BarList
            slot={3}
            data={data.launchRefusals.map((row) => ({
              key: row.reason,
              label: row.reason,
              value: row.count,
              display: String(row.count),
            }))}
            empty="None, which is the right number"
          />
        </Card>
      </div>
    </div>
  );
}

/**
 * One tile: a number, its direction, and the shape that got it there.
 *
 * A null value is "not measured" and never zero. The delta is omitted rather than shown as
 * +100% when the previous window was empty, because a percentage against nothing is noise.
 */
function Stat({
  label,
  value,
  previous,
  series,
  slot = 1,
}: {
  label: string;
  value: number | null | undefined;
  previous: number | null | undefined;
  series: number[] | null;
  slot?: 1 | 2 | 3;
}): React.ReactElement {
  const delta =
    typeof value === 'number' && typeof previous === 'number' && previous > 0
      ? Math.round(((value - previous) / previous) * 100)
      : null;

  return (
    <Card size="3">
      <div className="gb-stat">
        <Text size="2" color="gray">
          {label}
        </Text>
        <div className="gb-stat__row">
          <Heading size="7">
            {value === null || value === undefined ? '—' : value.toLocaleString()}
          </Heading>
          {series && series.length > 1 ? (
            <Sparkline values={series} slot={slot} label={label} />
          ) : null}
        </div>
        {delta !== null ? (
          <Text size="1" className={delta >= 0 ? 'gb-stat__delta--up' : 'gb-stat__delta--down'}>
            {`${delta >= 0 ? '+' : ''}${delta}% vs previous`}
          </Text>
        ) : (
          <Text size="1" color="gray">
            {value === null || value === undefined ? 'not measured' : 'no comparison yet'}
          </Text>
        )}
      </div>
    </Card>
  );
}
